import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'gatsby-plugin-intl'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import AccountService from 'src/services/AccountService'
import { setSnackbarValuesRoutine } from 'src/ducks/actions'
import Alert from 'src/components/Alert'
import TextInput from 'src/components/atoms/TextInput'
import Checkbox from 'src/components/atoms/CheckboxInput'
import { ButtonOutlined, ButtonWithLoader } from 'src/components/atoms/Buttons'
import {
  Column,
  Row,
  TitleContainer
} from 'src/features/account/components/atoms'
import { Title } from 'src/features/account/components/typography'
import {
  Dialog,
  DialogText,
  ActionBar,
  ConfirmButton,
  CancelButton
} from 'src/components/Dialog'

const WEBHOOK_QUERY_KEY = ['company-webhook-config']

const getResponsePayload = response => response?.data || response

const getErrorStatus = error => error?.status || error?.response?.status || null

const getErrorData = error => error?.data || error?.response?.data || {}

const getErrorMessage = error => {
  const message = getErrorData(error)?.message

  return typeof message === 'string' ? message : ''
}

const getErrorMessages = error => {
  const errors = getErrorData(error)?.errors

  if (!errors || typeof errors !== 'object') {
    return []
  }

  return Object.values(errors)
    .flat()
    .filter(Boolean)
}

const getWebhookUrlErrorMessage = error => {
  const errors = getErrorData(error)?.errors
  const urlErrors = errors?.webhookUrl || errors?.webhook_url

  if (Array.isArray(urlErrors) && urlErrors.length) {
    return urlErrors[0]
  }

  if (typeof urlErrors === 'string') {
    return urlErrors
  }

  return getErrorMessages(error)[0] || ''
}

const getWebhookUrl = config => config?.webhookUrl || config?.webhook_url || ''

const getWebhookEnabled = config => {
  if (typeof config?.webhookEnabled === 'boolean') {
    return config.webhookEnabled
  }

  if (typeof config?.webhook_enabled === 'boolean') {
    return config.webhook_enabled
  }

  return undefined
}

const getWebhookConfigured = config => {
  if (typeof config?.webhookConfigured === 'boolean') {
    return config.webhookConfigured
  }

  if (typeof config?.webhook_configured === 'boolean') {
    return config.webhook_configured
  }

  return Boolean(getWebhookUrl(config))
}

const getWebhookFormState = config => ({
  webhookUrl: getWebhookUrl(config),
  webhookEnabled:
    typeof getWebhookEnabled(config) === 'boolean'
      ? getWebhookEnabled(config)
      : true
})

const buildWebhookUpdatePayload = ({ webhookUrl, webhookEnabled }) => {
  const normalizedUrl = webhookUrl.trim()

  if (!normalizedUrl && !webhookEnabled) {
    return {
      webhookEnabled: false
    }
  }

  return {
    webhookUrl: normalizedUrl,
    webhookEnabled
  }
}

const buildWebhookQueryData = (config, fallbackValues = {}) => {
  const nextWebhookUrl =
    typeof getWebhookUrl(config) === 'string'
      ? getWebhookUrl(config)
      : fallbackValues.webhookUrl || ''
  const nextWebhookEnabled =
    typeof getWebhookEnabled(config) === 'boolean'
      ? getWebhookEnabled(config)
      : fallbackValues.webhookEnabled

  return {
    webhookUrl: nextWebhookUrl,
    webhookEnabled:
      typeof nextWebhookEnabled === 'boolean' ? nextWebhookEnabled : true,
    webhookConfigured: Boolean(getWebhookConfigured(config) || nextWebhookUrl)
  }
}

const isWebhookDeliveryActive = config =>
  Boolean(getWebhookUrl(config) && getWebhookEnabled(config))

const normalizeWebhookConfigResponse = response => getResponsePayload(response)

const extractWebhookSecret = response => {
  const payload = getResponsePayload(response)

  return (
    payload?.webhookSecret ||
    payload?.webhook_secret ||
    payload?.data?.webhookSecret ||
    payload?.data?.webhook_secret ||
    ''
  )
}

const copyToClipboard = value => {
  if (!value) {
    return
  }

  navigator?.clipboard?.writeText?.(value)
}

const getLoadErrorMessage = (error, intl) => {
  if (getErrorStatus(error) === 403) {
    return intl.formatMessage({ id: 'account.webhooks.accessError' })
  }

  return (
    getErrorMessage(error) ||
    intl.formatMessage({ id: 'account.webhooks.loadError' })
  )
}

const getUpdateErrorMessage = (error, intl) => {
  const status = getErrorStatus(error)

  if (status === 403) {
    return intl.formatMessage({ id: 'account.webhooks.accessError' })
  }

  if (status === 422) {
    return (
      getWebhookUrlErrorMessage(error) ||
      intl.formatMessage({ id: 'account.webhooks.urlValidationError' })
    )
  }

  return (
    getErrorMessage(error) ||
    intl.formatMessage({ id: 'account.webhooks.updateError' })
  )
}

const getRotateErrorMessage = (error, intl) => {
  const status = getErrorStatus(error)

  if (status === 403) {
    return intl.formatMessage({ id: 'account.webhooks.accessError' })
  }

  if (status === 400) {
    return (
      getErrorMessage(error) ||
      intl.formatMessage({
        id: 'account.webhooks.rotateRequiresUrlError'
      })
    )
  }

  return (
    getErrorMessage(error) ||
    intl.formatMessage({ id: 'account.webhooks.rotateError' })
  )
}

const WebhookSettings = ({ className, readOnly }) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const [webhookUrl, setWebhookUrl] = useState('')
  const [webhookEnabled, setWebhookEnabled] = useState(true)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isSecretDialogOpen, setIsSecretDialogOpen] = useState(false)
  const [webhookSecret, setWebhookSecret] = useState('')
  const [requestError, setRequestError] = useState('')

  const validateWebhookUrl = (value, enabled = webhookEnabled) => {
    const normalizedValue = value.trim()

    if (!normalizedValue) {
      if (!enabled) {
        return {
          valid: true,
          errors: []
        }
      }

      return {
        valid: false,
        errors: [intl.formatMessage({ id: 'account.webhooks.urlRequired' })]
      }
    }

    try {
      const parsedUrl = new URL(normalizedValue)

      if (['http:', 'https:'].includes(parsedUrl.protocol)) {
        if (parsedUrl.protocol !== 'https:') {
          return {
            valid: false,
            errors: [intl.formatMessage({ id: 'account.webhooks.urlInvalid' })]
          }
        }

        return {
          valid: true,
          errors: []
        }
      }
    } catch (error) {
      return {
        valid: false,
        errors: [intl.formatMessage({ id: 'account.webhooks.urlInvalid' })]
      }
    }

    return {
      valid: false,
      errors: [intl.formatMessage({ id: 'account.webhooks.urlInvalid' })]
    }
  }

  const webhookUrlValidation = useMemo(
    () => validateWebhookUrl(webhookUrl, webhookEnabled),
    [intl, webhookUrl, webhookEnabled]
  )

  const webhookQuery = useQuery({
    queryKey: WEBHOOK_QUERY_KEY,
    queryFn: AccountService.getCompanyWebhook,
    select: normalizeWebhookConfigResponse,
    refetchOnWindowFocus: false
  })

  const configQuery = webhookQuery
  const resolvedWebhookConfig = webhookQuery.data

  useEffect(() => {
    if (typeof resolvedWebhookConfig === 'undefined' || isEditDialogOpen) {
      return
    }

    const nextState = getWebhookFormState(resolvedWebhookConfig)
    setWebhookUrl(nextState.webhookUrl)
    setWebhookEnabled(nextState.webhookEnabled)
    setRequestError('')
  }, [resolvedWebhookConfig, isEditDialogOpen])

  const resetFormToCurrentConfig = () => {
    const nextState = getWebhookFormState(resolvedWebhookConfig)
    setWebhookUrl(nextState.webhookUrl)
    setWebhookEnabled(nextState.webhookEnabled)
    setRequestError('')
  }

  const syncWebhookQueries = nextQueryData => {
    queryClient.setQueryData(WEBHOOK_QUERY_KEY, nextQueryData)
  }

  const updateWebhookMutation = useMutation({
    mutationFn: payload => AccountService.updateCompanyWebhook(payload),
    onSuccess: async (response, variables) => {
      const nextConfig = normalizeWebhookConfigResponse(response)
      const nextQueryData = buildWebhookQueryData(nextConfig, {
        webhookUrl: variables.webhookUrl,
        webhookEnabled: variables.webhookEnabled
      })

      syncWebhookQueries(nextQueryData)
      setWebhookUrl(nextQueryData.webhookUrl)
      setWebhookEnabled(nextQueryData.webhookEnabled)

      dispatch(
        setSnackbarValuesRoutine({
          type: 'success',
          title: '',
          message: intl.formatMessage({ id: 'account.webhooks.updateSuccess' })
        })
      )

      setRequestError('')

      await queryClient.invalidateQueries({ queryKey: WEBHOOK_QUERY_KEY })

      setIsEditDialogOpen(false)

      const nextWebhookSecret = extractWebhookSecret(response)

      if (nextWebhookSecret) {
        copyToClipboard(nextWebhookSecret)
        setWebhookSecret(nextWebhookSecret)
        setIsSecretDialogOpen(true)
      }
    },
    onError: error => {
      setRequestError(getUpdateErrorMessage(error, intl))
    }
  })

  const rotateSecretMutation = useMutation({
    mutationFn: AccountService.rotateCompanyWebhookSecret,
    onSuccess: async response => {
      dispatch(
        setSnackbarValuesRoutine({
          type: 'success',
          title: '',
          message: intl.formatMessage({ id: 'account.webhooks.rotateSuccess' })
        })
      )

      setRequestError('')

      await queryClient.invalidateQueries({ queryKey: WEBHOOK_QUERY_KEY })

      setIsEditDialogOpen(false)

      const nextWebhookSecret = extractWebhookSecret(response)

      if (nextWebhookSecret) {
        copyToClipboard(nextWebhookSecret)
        setWebhookSecret(nextWebhookSecret)
        setIsSecretDialogOpen(true)
      }
    },
    onError: error => {
      setRequestError(getRotateErrorMessage(error, intl))
    }
  })

  const openEditDialog = () => {
    resetFormToCurrentConfig()
    setIsEditDialogOpen(true)
  }

  const closeEditDialog = () => {
    resetFormToCurrentConfig()
    setIsEditDialogOpen(false)
  }

  const closeSecretDialog = () => {
    setIsSecretDialogOpen(false)
    setWebhookSecret('')
  }

  const webhookConfigured = getWebhookConfigured(resolvedWebhookConfig)
  const webhookDeliveryActive = isWebhookDeliveryActive(resolvedWebhookConfig)
  const loadErrorMessage = configQuery.isError
    ? getLoadErrorMessage(configQuery.error, intl)
    : ''
  let helperMessageId = 'account.webhooks.notConfigured'

  if (readOnly) {
    helperMessageId = 'account.webhooks.readonlyInfo'
  } else if (webhookConfigured) {
    helperMessageId = 'account.webhooks.secretHidden'
  }

  const summaryUrl =
    getWebhookUrl(resolvedWebhookConfig) ||
    intl.formatMessage({ id: 'account.webhooks.urlMissing' })

  return (
    <div className={className}>
      <Row>
        <Column left>
          <TitleContainer>
            <Title>
              <FormattedMessage id='account.webhooks.title' />
            </Title>
          </TitleContainer>
          <DescriptionText>
            <FormattedMessage
              id={
                readOnly
                  ? 'account.webhooks.readonlyDescription'
                  : 'account.webhooks.info'
              }
            />
          </DescriptionText>
        </Column>
      </Row>

      <Row>
        <Column>
          <SettingsBox>
            {loadErrorMessage && (
              <Alert fullWidth type='alert' message={loadErrorMessage} />
            )}

            {configQuery.isLoading &&
              typeof resolvedWebhookConfig === 'undefined' && (
                <StateText>
                  <FormattedMessage id='common.loading' />
                </StateText>
              )}

            {!configQuery.isLoading && !loadErrorMessage && (
              <>
                <SummaryList>
                  <SummaryItem>
                    <SummaryLabel>
                      <FormattedMessage id='account.webhooks.summaryUrlLabel' />
                    </SummaryLabel>
                    <SummaryValue
                      isEmpty={!getWebhookUrl(resolvedWebhookConfig)}
                    >
                      {summaryUrl}
                    </SummaryValue>
                  </SummaryItem>

                  <SummaryItem>
                    <SummaryLabel>
                      <FormattedMessage id='account.webhooks.summaryStatusLabel' />
                    </SummaryLabel>
                    <SummaryValue>
                      <StatusInline>
                        <StatusDot active={webhookDeliveryActive} />
                        <FormattedMessage
                          id={
                            webhookDeliveryActive
                              ? 'account.webhooks.deliveryActive'
                              : 'account.webhooks.deliveryInactive'
                          }
                        />
                      </StatusInline>
                    </SummaryValue>
                  </SummaryItem>
                </SummaryList>

                <SubtleInfo>
                  <FormattedMessage id={helperMessageId} />
                </SubtleInfo>

                {!readOnly && (
                  <ActionsRow>
                    <ButtonOutlined active onClick={openEditDialog}>
                      <FormattedMessage id='common.edit' />
                    </ButtonOutlined>
                  </ActionsRow>
                )}
              </>
            )}

            {!configQuery.isLoading && loadErrorMessage && (
              <ActionsRow>
                <ButtonOutlined
                  active={!configQuery.isFetching}
                  loading={configQuery.isFetching}
                  onClick={() => {
                    setRequestError('')
                    configQuery.refetch()
                  }}
                >
                  <FormattedMessage id='account.webhooks.refreshButton' />
                </ButtonOutlined>
              </ActionsRow>
            )}
          </SettingsBox>
        </Column>
      </Row>

      {!readOnly && (
        <Dialog
          title={intl.formatMessage({
            id: 'account.webhooks.editDialogTitle'
          })}
          open={isEditDialogOpen}
          onClose={closeEditDialog}
        >
          <DialogText>
            <FormattedMessage id='account.webhooks.editDialogDescription' />
          </DialogText>

          {requestError && (
            <Alert fullWidth type='alert' message={requestError} />
          )}

          <DialogInputWrapper>
            <TextInput
              label={intl.formatMessage({ id: 'account.webhooks.urlLabel' })}
              name='webhookUrl'
              id='webhookUrl'
              placeholder={intl.formatMessage({
                id: 'account.webhooks.urlPlaceholder'
              })}
              helperText={intl.formatMessage({
                id: 'account.webhooks.urlHelper'
              })}
              value={webhookUrl}
              onChange={(_, value) => {
                setRequestError('')
                setWebhookUrl(value)

                if (!value.trim()) {
                  setWebhookEnabled(false)
                }
              }}
              validate={(_, value, completeValidation) =>
                completeValidation(validateWebhookUrl(value, webhookEnabled))
              }
            />
          </DialogInputWrapper>

          <DialogInputWrapper>
            <Checkbox
              value={webhookEnabled}
              option={{
                value: true,
                text: intl.formatMessage({
                  id: 'account.webhooks.enabledLabel'
                })
              }}
              onChange={() => {
                setRequestError('')
                setWebhookEnabled(value => !value)
              }}
              disabled={!webhookUrl.trim() && !webhookEnabled}
            />
          </DialogInputWrapper>

          <SubtleInfo>
            <FormattedMessage id={helperMessageId} />
          </SubtleInfo>

          <ModalActionBar>
            {webhookConfigured && (
              <ModalSecondaryButton
                active={!rotateSecretMutation.isLoading}
                loading={rotateSecretMutation.isLoading}
                onClick={() => {
                  setRequestError('')
                  rotateSecretMutation.mutate()
                }}
              >
                <FormattedMessage id='account.webhooks.rotateSecretButton' />
              </ModalSecondaryButton>
            )}

            <ModalCancelButton onClick={closeEditDialog}>
              <FormattedMessage id='common.cancel' />
            </ModalCancelButton>

            <SaveButton
              active={webhookUrlValidation.valid}
              loading={updateWebhookMutation.isLoading}
              disabled={
                !webhookUrlValidation.valid || updateWebhookMutation.isLoading
              }
              onClick={() => {
                if (!webhookUrlValidation.valid) {
                  return
                }

                setRequestError('')
                updateWebhookMutation.mutate(
                  buildWebhookUpdatePayload({
                    webhookUrl,
                    webhookEnabled
                  })
                )
              }}
            >
              <FormattedMessage id='common.save' />
            </SaveButton>
          </ModalActionBar>
        </Dialog>
      )}

      <Dialog
        title={intl.formatMessage({
          id: 'account.webhooks.secretGeneratedTitle'
        })}
        open={isSecretDialogOpen}
        onClose={closeSecretDialog}
      >
        <DialogText>
          <FormattedMessage id='account.webhooks.secretGeneratedDescription' />
        </DialogText>
        <DialogInputWrapper>
          <SecretRow>
            <TextInput
              label={intl.formatMessage({ id: 'account.webhooks.secret' })}
              name='webhookSecret'
              id='webhookSecret'
              value={webhookSecret}
              onChange={() => {}}
              validate={() => true}
              disabled
            />
            <CopyButton
              type='button'
              onClick={() => copyToClipboard(webhookSecret)}
              title={intl.formatMessage({
                id: 'account.webhooks.copyButton'
              })}
            >
              <FileCopyOutlinedIcon fontSize='small' />
            </CopyButton>
          </SecretRow>
        </DialogInputWrapper>
        <Alert
          fullWidth
          type='warning'
          message={intl.formatMessage({
            id: 'account.webhooks.secretWarning'
          })}
        />
        <ActionBar>
          <ConfirmButton onClick={closeSecretDialog}>
            <FormattedMessage id='common.close' />
          </ConfirmButton>
        </ActionBar>
      </Dialog>
    </div>
  )
}

export default styled(WebhookSettings)`
  ${Alert} {
    margin: 0;
  }
`

const SettingsBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`

const DescriptionText = styled.div`
  margin-bottom: 0.25rem;
  font-size: 0.8rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.7).hex()};
`

const SummaryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`

const SummaryLabel = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.6).hex()};
`

const SummaryValue = styled.div`
  font-size: 0.9rem;
  line-height: 1.45;
  color: ${({ theme, isEmpty }) =>
    isEmpty
      ? theme.colors.ebonyClay.alpha(0.55).hex()
      : theme.colors.ebonyClay.hex()};
  word-break: break-word;
`

const StatusInline = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  gap: 0.45rem;
`

const StatusDot = styled.span`
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ theme, active }) =>
    active ? theme.colors.apple.hex() : theme.colors.fadedRed.hex()};
  box-shadow: 0 0 0 3px
    ${({ theme, active }) =>
      active
        ? theme.colors.apple.alpha(0.16).hex()
        : theme.colors.fadedRed.alpha(0.14).hex()};
`

const SubtleInfo = styled.div`
  font-size: 0.8rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.7).hex()};
`

const StateText = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.6).hex()};
`

const SaveButton = styled(ButtonWithLoader)`
  background-color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  box-shadow: 0 2px 10px 0 rgba(36, 45, 63, 0.25);
  font-weight: 800;
  color: ${({ theme }) => theme.colors.white.hex()};
  white-space: nowrap;
  min-height: 2.5rem;
  padding: 0.6rem 1rem;
  font-size: 0.85rem;

  ${({ active }) =>
    !active &&
    `
      opacity: 0.65;
    `}
`

const ModalSecondaryButton = styled(ButtonOutlined)`
  min-height: 2.5rem;
  padding: 0.6rem 1rem;
  font-size: 0.85rem;
`

const ModalCancelButton = styled(CancelButton)`
  min-height: 2.5rem;
  padding: 0.6rem 1rem;
  font-size: 0.85rem;
`

const ActionsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;

  ${SaveButton},
  ${ButtonOutlined} {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    min-width: auto;
    height: 2.25rem;
    line-height: 1;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
`

const ModalActionBar = styled(ActionBar)`
  ${SaveButton},
  ${ModalSecondaryButton},
  ${ModalCancelButton} {
    min-width: 0;
    height: 2.5rem;
    box-sizing: border-box;
  }
`

const DialogInputWrapper = styled.div`
  margin: 1rem 0;
`

const SecretRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  margin-bottom: 1rem;
  align-items: center;

  @media (min-width: 768px) {
    grid-template-columns: minmax(0, 1fr) auto;
  }
`

const CopyButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.4).hex()};
  padding: 0.5rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};

  &:hover {
    background: ${({ theme }) => theme.colors.ghost.alpha(0.2).hex()};
  }
`
